import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from './api';

// Per-id cache of authenticated fetches converted to blob: URLs. The blob URL
// is origin-bound and not useful if copied out of the app — only the browser
// session that created it can dereference it. We never put the authed media
// URL into the DOM, so there's nothing leakable to inspect.
//
// Implementation notes:
// - React Query caches the underlying blob so concurrent consumers share one
//   fetch. `gcTime: Infinity` keeps the blob alive until the query is evicted.
// - We return the object URL through a small `useEffect` that creates +
//   revokes per-consumer to avoid dangling URLs after unmount. React Query's
//   shared blob remains valid; consumers just stop pointing at their URL.

async function fetchBlob(id: string, getToken: () => Promise<string | null>): Promise<Blob> {
  const token = await getToken();
  const res = await fetch(`${API_BASE_URL}/media/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`media ${res.status}`);
  return await res.blob();
}

// Global token getter injected at bootstrap (same as api.ts). We import from
// there indirectly by reading window when needed.
let tokenGetter: (() => Promise<string | null>) | null = null;
export function setMediaTokenGetter(getter: (() => Promise<string | null>) | null) {
  tokenGetter = getter;
}

export function useMediaBlobUrl(id: string | null | undefined): string | null {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['media-blob', id],
    queryFn: () =>
      fetchBlob(id!, async () => (tokenGetter ? await tokenGetter() : null)),
    enabled: Boolean(id),
    gcTime: Infinity,
    staleTime: Infinity,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(query.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [query.data]);

  // Let callers force-drop a cached blob (e.g., after delete) so a subsequent
  // view re-fetches fresh bytes.
  useEffect(() => {
    if (query.error) qc.removeQueries({ queryKey: ['media-blob', id] });
  }, [query.error, id, qc]);

  return url;
}
